// react
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Step } from 'react-joyride';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';

// material ui icons
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';

// material ui
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

// api
import { useLazyDataGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/data.enhanced';
import type { ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import { getExportDataTour } from '@/components/joyride/guided-tours/ExportData.tour';
import useAppTour from '@/components/joyride/useAppTour';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import { RHFFieldLabel } from '@/components/ui/form';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
// Redux and API
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';
import { inputSurfaceStyles } from '@/theme/Inputs/textFieldCustomization';

import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';

// Column configurations for unit data export
const INITIAL_COLUMNS = [
  { field: 'Timestamp', headerName: 'Timestamp', required: false },
  { field: 'Value', headerName: 'Value', required: false },
  { field: 'UnitId', headerName: 'Unit ID', required: false },
  { field: 'UnitName', headerName: 'Unit Name', required: false },
  { field: 'DataType', headerName: 'Data Type', required: false },
  { field: 'AggregateType', headerName: 'Aggregate Type', required: false },
  {
    field: 'UnitTypeInputId',
    headerName: 'Unit Type Input ID',
    required: false,
  },
  {
    field: 'InputDescription',
    headerName: 'Description',
    required: false,
  },
  { field: 'Base', headerName: 'Base', required: false },
];

const getTimestampZone = (mode) => {
  if (mode === 'browser') return DateTime.local().zoneName || 'UTC';
  return 'UTC';
};

export const Route = createFileRoute('/$accountId/_user/export-data/')({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: { crumb: 'glossary:export-data' },
});

function RouteComponent() {
  const { t } = useTranslation('export');
  const dispatch = useDispatch();
  const search = useSearch({ strict: false });
  const hasAutoStartedTourRef = useRef(false);

  const { accountId } = useParams({ strict: false });

  const { controls, Tour } = useAppTour({
    steps: getExportDataTour() as Step[],
  });

  useEffect(() => {
    if (search?.tour === 'export-data' && !hasAutoStartedTourRef.current) {
      hasAutoStartedTourRef.current = true;
      controls.start();
    } else if (!search?.tour) {
      controls.close();
    }
  }, [controls, search?.tour]);

  // Fetch units
  const { data: units = [], isLoading: isLoadingUnits } =
    useUnitsGetCollectionAsyncByqueryOptionsQuery(
      {
        'Account.AccountId': `eq(${accountId})`,
        top: 1000,
      },
      {
        skip: !accountId,
      },
    );

  // State
  const [selectedUnits, setSelectedUnits] = useState<ReadUnitDto[]>([]);
  const [columnOrder, setColumnOrder] = useState(
    INITIAL_COLUMNS.map((col) => col.field),
  );
  const [columnVisibility, setColumnVisibility] = useState(
    INITIAL_COLUMNS.reduce((acc, col) => {
      acc[col.field] = col.required ? true : true; // All visible by default
      return acc;
    }, {}),
  );
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState('xlsx'); // 'xlsx' or 'csv'
  const [aggregateType, setAggregateType] = useState('Hour'); // 'Raw', 'Minutes5', 'Hour'
  const [timestampZoneMode, setTimestampZoneMode] = useState('utc'); // 'utc', 'copenhagen', 'browser'

  const ZONE = 'Europe/Copenhagen';

  // Date range state (default: last 7 days) — always in Copenhagen time
  const [startDate, setStartDate] = useState(() => {
    return DateTime.now().setZone(ZONE).minus({ days: 7 }).startOf('day');
  });
  const [presetDays, setPresetDays] = useState<number | null>(7);
  const [endDate, setEndDate] = useState(() => {
    return DateTime.now().setZone(ZONE);
  });

  // Lazy query for fetching data
  const [fetchData] = useLazyDataGetCollectionAsyncByqueryOptionsQuery();
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Get selected columns in order
  const selectedColumnsInOrder = useMemo(() => {
    return columnOrder.filter((field) => columnVisibility[field]);
  }, [columnOrder, columnVisibility]);

  // Get ordered columns with metadata, with disabled columns moved to the right
  const orderedColumns = useMemo(() => {
    const columns = columnOrder
      .map((field) => INITIAL_COLUMNS.find((col) => col.field === field))
      .filter(Boolean);

    // Sort: enabled columns first, then disabled columns
    return columns.sort((a, b) => {
      const aEnabled = columnVisibility[a.field];
      const bEnabled = columnVisibility[b.field];
      if (aEnabled === bEnabled) return 0;
      return aEnabled ? -1 : 1;
    });
  }, [columnOrder, columnVisibility]);

  const timestampZone = getTimestampZone(timestampZoneMode);
  const timestampHeader = `Timestamp (${timestampZone})`;
  const getHeaderName = (column) =>
    column.field === 'Timestamp' ? timestampHeader : column.headerName;

  // Sample preview data
  const previewData = (() => {
    const sample = (hoursAgo) =>
      DateTime.now()
        .setZone(timestampZone)
        .minus({ hours: hoursAgo })
        .toFormat('dd-MM-yyyy HH:mm:ss');
    return [
      {
        Timestamp: sample(2),
        UnitId: 1234,
        UnitName: '<Name>',
        Value: 1250.5,
        DataType: 'power',
        AggregateType: 'Hour',
        UnitTypeInputId: 3001,
        InputDescription: 'Phase 1',
        Base: 'W',
      },
      {
        Timestamp: sample(1),
        UnitId: 1234,
        UnitName: '<Name>',
        Value: 1320.8,
        DataType: 'power',
        AggregateType: 'Hour',
        UnitTypeInputId: 3002,
        InputDescription: 'Phase 2',
        Base: 'W',
      },
    ];
  })();

  // Toggle column visibility
  const handleToggleColumn = (field) => {
    const column = INITIAL_COLUMNS.find((col) => col.field === field);
    if (column?.required) return; // Cannot disable required columns

    setColumnVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Select all columns
  const handleSelectAll = () => {
    const newVisibility = {};
    INITIAL_COLUMNS.forEach((col) => {
      newVisibility[col.field] = true;
    });
    setColumnVisibility(newVisibility);
  };

  // Deselect all non-required columns
  const handleDeselectAll = () => {
    const newVisibility = {};
    INITIAL_COLUMNS.forEach((col) => {
      newVisibility[col.field] = col.required;
    });
    setColumnVisibility(newVisibility);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedColumnIndex(index);
    setDragOverIndex(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedColumnIndex === null || draggedColumnIndex === index) return;

    // Only update visual indicator, don't reorder yet
    setDragOverIndex(index);
  };

  const handleDragEnd = (e, dropIndex) => {
    e.preventDefault();

    if (
      draggedColumnIndex === null ||
      dropIndex === null ||
      draggedColumnIndex === dropIndex
    ) {
      setDraggedColumnIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Get the fields in their current displayed order
    const displayedFields = orderedColumns.map((col) => col.field);

    // Reorder based on drag operation
    const newOrderedFields = [...displayedFields];
    const draggedField = newOrderedFields[draggedColumnIndex];
    newOrderedFields.splice(draggedColumnIndex, 1);
    newOrderedFields.splice(dropIndex, 0, draggedField);

    // Update the column order state
    setColumnOrder(newOrderedFields);
    setDraggedColumnIndex(null);
    setDragOverIndex(null);
  };

  // Fetch data for all selected units
  const fetchAllUnitData = async () => {
    const from = startDate.toUTC().toISO();
    const to = endDate.toUTC().toISO();

    const allData = [];

    for (const unit of selectedUnits) {
      // Use RecordedDataTypes from the unit
      const recordedDataTypes = unit.RecordedDataTypes || [];

      for (const recordedType of recordedDataTypes) {
        const unitTypeInputId =
          recordedType.UnitTypeInput?.UnitTypeInputId || 0;
        const inputName = recordedType.UnitTypeInput?.Name || 'Total';
        const dataTypes = recordedType.DataTypes || [];

        // Fetch for each recorded data type
        for (const dataType of dataTypes) {
          try {
            const result = await fetchData({
              timestamp: [`ge(${from})`, `lt(${to})`],
              unitId: `eq(${unit.UnitId})`,
              aggregateType: `eq(${aggregateType})`,
              dataType: `eq(${dataType})`,
              unitTypeInputId: `eq(${unitTypeInputId === 0 ? null : unitTypeInputId})`,
              top: 10000,
            }).unwrap();

            // Process and store data
            if (result && Array.isArray(result)) {
              result.forEach((item) => {
                allData.push({
                  Timestamp: item.Timestamp,
                  UnitId: item.UnitId || unit.UnitId,
                  UnitName: unit.Name,
                  Value: item.Value,
                  DataType: item.DataType || dataType,
                  AggregateType: item.AggregateType || aggregateType,
                  UnitTypeInputId: item.UnitTypeInputId || unitTypeInputId,
                  InputDescription: inputName,
                  Base: item.Base || '',
                });
              });
            }
          } catch (error) {
            // Skip this combination if fetch fails
            console.error(
              `Failed to fetch data for Unit:${unit.UnitId}, Input:${unitTypeInputId}, Type:${dataType}, Agg:${aggregateType}`,
              error,
            );
          }
        }
      }
    }

    return allData;
  };

  // Process and filter data based on selected columns in order
  const processDataForExport = (data) => {
    return data.map((row) => {
      const processedRow = {};

      selectedColumnsInOrder.forEach((columnField) => {
        if (columnField === 'Timestamp') {
          // API timestamps are UTC; convert to the selected zone (default UTC)
          const dt = DateTime.fromISO(row.Timestamp, {
            zone: 'utc',
          }).setZone(timestampZone);
          processedRow[timestampHeader] = dt.toFormat('dd-MM-yyyy HH:mm:ss');
        } else {
          processedRow[columnField] = row[columnField];
        }
      });

      return processedRow;
    });
  };

  // Unified export handler
  const handleExport = async () => {
    if (selectedUnits.length === 0) {
      dispatch(
        showSnackbar({
          message:
            t?.('feedback.noUnitsSelected') ||
            'Please select at least one unit',
          severity: 'warning',
        }),
      );
      return;
    }

    if (selectedColumnsInOrder.length === 0) {
      dispatch(
        showSnackbar({
          message:
            t?.('feedback.noColumnsSelected') ||
            'Please select at least one column',
          severity: 'warning',
        }),
      );
      return;
    }

    setIsExporting(true);

    try {
      // Fetch all data
      const rawData = await fetchAllUnitData();

      if (rawData.length === 0) {
        dispatch(
          showSnackbar({
            message:
              t?.('feedback.noData') ||
              'No data available for the selected criteria',
            severity: 'info',
          }),
        );
        setIsExporting(false);
        return;
      }

      // Process data
      const processedData = processDataForExport(rawData);

      if (exportType === 'xlsx') {
        // Create workbook
        const worksheet = XLSX.utils.json_to_sheet(processedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');

        // Generate filename with timestamp
        const filename = `unit-data-export-${DateTime.now().toFormat('yyyy-MM-dd-HHmmss')}.xlsx`;

        // Download
        XLSX.writeFile(workbook, filename);

        dispatch(
          showSnackbar({
            message:
              t?.('feedback.success', { format: 'Excel' }) ||
              `Successfully exported ${rawData.length} rows to Excel`,
            severity: 'success',
          }),
        );
      } else {
        // Convert to CSV
        const worksheet = XLSX.utils.json_to_sheet(processedData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);

        // Create blob and download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const filename = `unit-data-export-${DateTime.now().toFormat('yyyy-MM-dd-HHmmss')}.csv`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        dispatch(
          showSnackbar({
            message:
              t?.('feedback.success', { format: 'CSV' }) ||
              `Successfully exported ${rawData.length} rows to CSV`,
            severity: 'success',
          }),
        );
      }
    } catch (error) {
      console.error('Export error:', error);
      dispatch(
        showSnackbar({
          message: t?.('feedback.error') || 'Failed to export data',
          severity: 'error',
        }),
      );
    } finally {
      setIsExporting(false);
    }
  };

  useAlertProvider({
    enabled: !(selectedUnits.length > 0 && selectedColumnsInOrder.length > 0),
    severity: 'info',
    message: t('exportStatus.selectRequired'),
  });

  // Handle date range preset shortcuts
  const handlePresetRange = (days) => {
    const end = DateTime.now().setZone(ZONE);
    const start = DateTime.now().setZone(ZONE).minus({ days }).startOf('day');

    setStartDate(start);
    setEndDate(end);
    setPresetDays(days);
  };

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      {Tour}
      <PageHeader
        title={t('actions.exportItem', { item: 'Data', ns: 'common' })}
      >
        {' '}
        <ToggleButtonGroup
          data-tour="export-data-format"
          value={exportType}
          exclusive
          onChange={(_, newValue) => newValue && setExportType(newValue)}
          size="small"
        >
          <ToggleButton value="xlsx">
            <i
              className="fas fa-file-excel"
              style={{ marginRight: 6, fontSize: 16, color: '#217346' }}
            />
            {t('formats.excel')}
          </ToggleButton>
          <ToggleButton value="csv">
            <i
              className="fas fa-file-csv"
              style={{ marginRight: 6, fontSize: 16, color: '#2f5aaa' }}
            />
            {t('formats.csv')}
          </ToggleButton>
        </ToggleButtonGroup>
      </PageHeader>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            {/* Select Units */}
            <Box data-tour="export-data-units">
              <Autocomplete
                multiple
                options={units}
                value={selectedUnits}
                onChange={(_, newValue) => setSelectedUnits(newValue)}
                getOptionLabel={(option) => `${option.Name} (${option.UnitId})`}
                isOptionEqualToValue={(option, value) =>
                  option.UnitId === value.UnitId
                }
                loading={isLoadingUnits}
                renderInput={(params) => (
                  <Box>
                    <RHFFieldLabel htmlFor={params.id}>
                      {t('actions.selectItem', {
                        item: 'Units',
                        ns: 'common',
                      })}
                    </RHFFieldLabel>
                    <TextField
                      {...params}
                      placeholder={t('common:actions.selectItem', {
                        item: 'Units',
                      })}
                    />
                  </Box>
                )}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getItemProps({ index })}
                      key={option.UnitId}
                      icon={
                        <i
                          className="fas fa-lightbulb"
                          style={{ fontSize: 12 }}
                        />
                      }
                      label={`${option.Name} (${option.UnitId})`}
                      size="small"
                    />
                  ))
                }
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue?.toLowerCase() || '';
                  return options.filter(
                    (option) =>
                      option.Name?.toLowerCase().includes(searchTerm) ||
                      option.UnitId.toString().includes(searchTerm),
                  );
                }}
              />
              {selectedUnits.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  {t('unitsSelected', { count: selectedUnits.length })}
                </Typography>
              )}
            </Box>

            {/* Date Range & Aggregate Type */}
            <Box data-tour="export-data-date-range">
              <Grid container spacing={2} alignItems="center">
                <Grid
                  data-tour="export-data-aggregation"
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <RHFFieldLabel htmlFor="export-start-date">
                    {t('prepositions.fromItem', {
                      item: 'Dato',
                      ns: 'common',
                    })}
                  </RHFFieldLabel>
                  <DateTimePicker
                    value={startDate}
                    onChange={(newValue) => {
                      setPresetDays(null);
                      setStartDate(
                        newValue?.setZone(ZONE, { keepLocalTime: true }) ??
                          newValue,
                      );
                    }}
                    timezone={ZONE}
                    maxDateTime={endDate}
                    slotProps={{
                      textField: {
                        id: 'export-start-date',
                        fullWidth: true,
                        variant: 'filled',
                        InputProps: {
                          disableUnderline: true,
                          sx: { borderRadius: 1 },
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <RHFFieldLabel htmlFor="export-end-date">
                    {t('prepositions.toItem', {
                      item: 'Dato',
                      ns: 'common',
                    })}
                  </RHFFieldLabel>
                  <DateTimePicker
                    value={endDate}
                    onChange={(newValue) => {
                      setPresetDays(null);
                      setEndDate(
                        newValue?.setZone(ZONE, { keepLocalTime: true }) ??
                          newValue,
                      );
                    }}
                    timezone={ZONE}
                    minDateTime={startDate}
                    maxDateTime={DateTime.now().setZone(ZONE)}
                    slotProps={{
                      textField: {
                        id: 'export-end-date',
                        fullWidth: true,
                        variant: 'filled',
                        InputProps: {
                          disableUnderline: true,
                          sx: { borderRadius: 1 },
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <FormControl fullWidth>
                    <RHFFieldLabel htmlFor="aggregate-type-select">
                      {t('aggregateType.label')}
                    </RHFFieldLabel>
                    <Select
                      id="aggregate-type-select"
                      value={aggregateType}
                      onChange={(e) => setAggregateType(e.target.value)}
                      variant="outlined"
                      fullWidth
                      sx={inputSurfaceStyles}
                    >
                      <MenuItem value="Raw">
                        <i
                          className="fas fa-chart-line"
                          style={{ marginRight: 8 }}
                        />
                        {t('aggregateType.raw')}
                      </MenuItem>
                      <MenuItem value="Minutes5">
                        <i
                          className="fas fa-clock"
                          style={{ marginRight: 8 }}
                        />
                        {t('aggregateType.minutes5')}
                      </MenuItem>
                      <MenuItem value="Hour">
                        <i
                          className="fas fa-hourglass-half"
                          style={{ marginRight: 8 }}
                        />
                        {t('aggregateType.hour')}
                      </MenuItem>
                      <MenuItem value="Day">
                        <i
                          className="fas fa-calendar-day"
                          style={{ marginRight: 8 }}
                        />
                        {t('aggregateType.day')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <RHFFieldLabel htmlFor="timestamp-zone-select">
                      {t('timestampZone.label')}
                    </RHFFieldLabel>
                    <Select
                      id="timestamp-zone-select"
                      value={timestampZoneMode}
                      onChange={(e) => setTimestampZoneMode(e.target.value)}
                      variant="outlined"
                      fullWidth
                      sx={inputSurfaceStyles}
                    >
                      <MenuItem value="utc">{t('timestampZone.utc')}</MenuItem>
                      {/* <MenuItem value="copenhagen">
                        {t('timestampZone.copenhagen')}
                      </MenuItem> */}
                      <MenuItem value="browser">
                        {t('timestampZone.browser', {
                          zone: getTimestampZone('browser'),
                        })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Quick presets */}
              <Box mt={2}>
                <ToggleButtonGroup
                  value={presetDays}
                  exclusive
                  onChange={(_, days) => days && handlePresetRange(days)}
                  size="small"
                >
                  <ToggleButton value={7}>
                    {t('presets.last7Days')}
                  </ToggleButton>
                  <ToggleButton value={14}>
                    {t('presets.last14Days')}
                  </ToggleButton>
                  <ToggleButton value={30}>
                    {t('presets.last30Days')}
                  </ToggleButton>
                  <ToggleButton value={90}>
                    {t('presets.last90Days')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* Column Configuration & Preview */}
            <Box data-tour="export-data-columns">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {/* <i className="fas fa-columns" style={{ marginRight: 8 }} /> */}
                  {t('columnConfiguration.title')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    variant="outlined"
                  >
                    {t('actions.selectItem', { item: 'Alle', ns: 'common' })}
                  </Button>
                  <Button
                    size="small"
                    onClick={handleDeselectAll}
                    variant="outlined"
                  >
                    {t('actions.deselectItem', { item: 'Alle', ns: 'common' })}
                  </Button>
                </Stack>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                {t('columnConfiguration.helpText')}
              </Typography>

              <TableContainer
                data-tour="export-data-preview"
                sx={{
                  maxHeight: 450,
                  overflowX: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      {orderedColumns.map((column, index) => (
                        <TableCell
                          key={column.field}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDragEnd(e, index)}
                          onDragEnd={(e) => handleDragEnd(e, dragOverIndex)}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: columnVisibility[column.field]
                              ? 'action.hover'
                              : 'action.disabledBackground',
                            opacity: columnVisibility[column.field] ? 1 : 0.5,
                            minWidth: 150,
                            cursor: 'grab',
                            userSelect: 'none',
                            borderRight: '1px solid',
                            borderRightColor: 'divider',
                            transition: 'all 0.15s ease',
                            '&:active': {
                              cursor: 'grabbing',
                            },
                            ...(draggedColumnIndex === index && {
                              opacity: 0.5,
                              borderLeft: '3px solid',
                              borderLeftColor: 'primary.main',
                              borderRight: '3px solid',
                              borderRightColor: 'primary.main',
                            }),
                            ...(dragOverIndex === index &&
                              draggedColumnIndex !== index && {
                                borderLeft: '2px dashed',
                                borderLeftColor: 'primary.light',
                              }),
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '.MuiTableCell-root:hover &': {
                                  opacity: 1,
                                },
                              }}
                            >
                              <DragIndicatorRoundedIcon fontSize="small" />
                            </Box>
                            <Typography variant="caption" fontWeight={600}>
                              {getHeaderName(column)}
                              {column.required && ' *'}
                            </Typography>
                            <Checkbox
                              checked={columnVisibility[column.field]}
                              onChange={() => handleToggleColumn(column.field)}
                              disabled={column.required}
                              size="small"
                              sx={{ padding: 0.25 }}
                            />
                          </Stack>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((row, rowIndex) => (
                      <TableRow key={rowIndex} hover>
                        {orderedColumns.map((column) => (
                          <TableCell
                            key={column.field}
                            sx={{
                              opacity: columnVisibility[column.field] ? 1 : 0.4,
                              backgroundColor: columnVisibility[column.field]
                                ? 'inherit'
                                : 'action.disabledBackground',
                              borderRight: '1px solid',
                              borderRightColor: 'divider',
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 140 }}
                            >
                              {row[column.field] ?? '-'}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
              >
                {/* <i className="fas fa-info-circle" style={{ marginRight: 4 }} /> */}
                {t('columnConfiguration.columnsToExport', {
                  count: selectedColumnsInOrder.length,
                })}
                {INITIAL_COLUMNS.some((col) => col.required) &&
                  ` ${t('columnConfiguration.requiredColumnsInfo')}`}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          data-tour="export-data-submit"
          variant="contained"
          color="primary"
          onClick={handleExport}
          disabled={
            isExporting ||
            selectedUnits.length === 0 ||
            selectedColumnsInOrder.length === 0
          }
          startIcon={
            isExporting ? (
              <CircularProgress size={20} />
            ) : (
              <i
                className={
                  exportType === 'xlsx'
                    ? 'fas fa-file-excel'
                    : 'fas fa-file-csv'
                }
                style={{
                  color: exportType === 'xlsx' ? '#217346' : '#2f5aaa',
                }}
              />
            )
          }
        >
          {isExporting
            ? t('exportStatus.exporting')
            : t('exportStatus.exportTo', {
                format: exportType === 'xlsx' ? 'Excel' : 'CSV',
              })}
        </Button>
      </Box>
    </PageContainer>
  );
}
